import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faClock, faPaperPlane, faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import {
  Card,
  Form,
  Button,
  Table,
  InputGroup,
  FormControl,
  Badge,
  Pagination,
  Row,
  Col,
  FormCheck
} from '@themesberg/react-bootstrap';
import { collection, query, orderBy, onSnapshot, addDoc, where, limit, getDocs } from 'firebase/firestore';
import { db, functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';

export default () => {
  // Form state
  const [formData, setFormData] = useState(() => ({
    selectedUsers: [],
    title: "",
    message: "",
    scheduleTime: false,
    scheduledHour: "12",
    scheduledMinute: "00",
    isDaily: false
  }));

  // Users state
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // Notifications state
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduledPage, setScheduledPage] = useState(1);
  const [scheduledPageSize, setScheduledPageSize] = useState(5);
  const [scheduledTotalPages, setScheduledTotalPages] = useState(1);

  // Türkiye saati için helper
  const getTurkeyTime = () => {
    const now = new Date();
    const turkeyTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Istanbul" }));
    return turkeyTime;
  };

  // Kullanıcı arama (server-side)
  useEffect(() => {
    // Arama terimi boşsa veya çok kısaysa listeyi temizle
    if (!searchTerm.trim()) {
      setFilteredUsers([]);
      return;
    }

    if (searchTerm.length < 2) return;

    const delayDebounceFn = setTimeout(async () => {
      setLoading(true);
      try {
        const usersRef = collection(db, 'users');

        // İsme göre arama
        const qName = query(
          usersRef,
          where('name', '>=', searchTerm),
          where('name', '<=', searchTerm + '\uf8ff'),
          limit(50)
        );

        const qEmail = query(
          usersRef,
          where('email', '>=', searchTerm),
          where('email', '<=', searchTerm + '\uf8ff'),
          limit(50)
        );

        const [snapshotName, snapshotEmail] = await Promise.all([
          getDocs(qName),
          getDocs(qEmail)
        ]);

        const usersMap = new Map();

        const processSnapshot = (snapshot) => {
          snapshot.forEach(doc => {
            const data = doc.data();
            if (data.isTestUser !== true && data.isAdmin !== true) {
              const fullName = data.name ||
                (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` :
                  data.firstName || data.lastName || 'İsimsiz');

              usersMap.set(doc.id, {
                id: doc.id,
                name: fullName,
                email: data.email || '',
                profileImageUrl: data.profileImageUrl || null
              });
            }
          });
        };

        processSnapshot(snapshotName);
        processSnapshot(snapshotEmail);

        const results = Array.from(usersMap.values());
        setFilteredUsers(results);
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Planlanmış bildirimleri yükle
  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      where('status', '==', 'scheduled')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications = [];
      snapshot.forEach(doc => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        });
      });
      // Client-side sıralama (eğer orderBy çalışmazsa)
      notifications.sort((a, b) => {
        const timeA = a.scheduledTime?.toDate?.() || new Date(0);
        const timeB = b.scheduledTime?.toDate?.() || new Date(0);
        return timeA - timeB;
      });
      setScheduledNotifications(notifications);
      setScheduledTotalPages(Math.ceil(notifications.length / scheduledPageSize));
    }, (error) => {
      console.error("Error loading scheduled notifications:", error);
    });

    return () => unsubscribe();
  }, [scheduledPageSize]);

  // Gönderilen bildirimleri yükle (son 10)
  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      where('status', '==', 'sent')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifications = [];
      snapshot.forEach(doc => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        });
      });
      // Client-side sıralama ve limit (eğer orderBy/limit çalışmazsa)
      notifications.sort((a, b) => {
        const timeA = a.sentAt?.toDate?.() || new Date(0);
        const timeB = b.sentAt?.toDate?.() || new Date(0);
        return timeB - timeA; // En yeni önce
      });
      setSentNotifications(notifications.slice(0, 10)); // Son 10
    }, (error) => {
      console.error("Error loading sent notifications:", error);
    });

    return () => unsubscribe();
  }, []);

  // Kullanıcı seçimi
  const handleUserSelect = (userId) => {
    setFormData(prev => {
      const isSelected = prev.selectedUsers.includes(userId);
      if (isSelected) {
        return { ...prev, selectedUsers: prev.selectedUsers.filter(id => id !== userId) };
      } else {
        return { ...prev, selectedUsers: [...prev.selectedUsers, userId] };
      }
    });
  };

  // Tümünü seç/kaldır
  const handleSelectAll = () => {
    if (selectAll) {
      setFormData(prev => ({ ...prev, selectedUsers: [] }));
      setSelectAll(false);
    } else {
      setFormData(prev => ({ ...prev, selectedUsers: filteredUsers.map(u => u.id) }));
      setSelectAll(true);
    }
  };

  // Bildirim gönder
  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!formData || !formData.title || !formData.message) {
      alert("Başlık ve mesaj zorunludur!");
      return;
    }

    setLoading(true);
    try {
      const sendNotification = httpsCallable(functions, 'sendNotification');

      let scheduledTime = null;
      if (formData.scheduleTime) {
        const turkeyTime = getTurkeyTime();
        const [hour, minute] = [parseInt(formData.scheduledHour), parseInt(formData.scheduledMinute)];
        const scheduledDate = new Date(turkeyTime);
        scheduledDate.setHours(hour, minute, 0, 0);

        // Eğer seçilen saat geçmişse, yarın için ayarla
        if (scheduledDate < turkeyTime) {
          scheduledDate.setDate(scheduledDate.getDate() + 1);
        }

        scheduledTime = scheduledDate.toISOString();
      }

      const result = await sendNotification({
        title: formData.title,
        message: formData.message,
        userIds: formData.selectedUsers.length > 0 ? formData.selectedUsers : null,
        scheduledTime: scheduledTime,
        isDaily: formData.isDaily
      });

      // Formu temizle
      setFormData({
        selectedUsers: [],
        title: "",
        message: "",
        scheduleTime: false,
        scheduledHour: "12",
        scheduledMinute: "00",
        isDaily: false
      });
      setSelectAll(false);
      setShowUserSelect(false);

      if (result.data.success) {
        const sentCount = result.data.sentCount || 0;
        const failedCount = result.data.failedCount || 0;
        if (sentCount > 0) {
          alert(`Bildirim başarıyla gönderildi! ${sentCount} kullanıcıya ulaştı${failedCount > 0 ? `, ${failedCount} kullanıcıya ulaşamadı` : ''}.`);
        } else {
          alert(`Bildirim kaydı oluşturuldu ancak hiçbir kullanıcıya gönderilemedi.\n\nSebep: Seçilen kullanıcılarda FCM token bulunamadı.\n\nÇözüm: Kullanıcıların uygulamayı açıp bildirim izni vermesi gerekiyor.`);
        }
      } else {
        alert("Bildirim gönderilirken hata oluştu: " + (result.data.error || "Bilinmeyen hata"));
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      const errorMessage = error.message || error.toString();
      const details = error.details || error.code || "";

      // FCM token hatası için özel mesaj
      if (errorMessage.includes("No valid FCM tokens") || errorMessage.includes("invalid-argument")) {
        alert(`Bildirim gönderilemedi!\n\nSebep: Seçilen kullanıcılarda FCM token bulunamadı.\n\nÇözüm:\n1. Kullanıcıların uygulamayı açması gerekiyor\n2. Bildirim izni vermeleri gerekiyor (Android 13+)\n3. Token otomatik olarak kaydedilecek\n\nNot: Şu anda hiçbir kullanıcıda FCM token yok. Kullanıcılar uygulamayı açtığında token otomatik oluşacak.`);
      } else {
        alert(`Bildirim gönderilirken hata oluştu: ${errorMessage}${details ? `\nDetay: ${details}` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Zamanlanmış bildirimleri sayfalama
  const getScheduledNotificationsPage = () => {
    const start = (scheduledPage - 1) * scheduledPageSize;
    const end = start + scheduledPageSize;
    return scheduledNotifications.slice(start, end);
  };

  // Tarih formatla
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Saat formatla
  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <>
      <div className="py-4">
        <h4>Bildirimler</h4>
        <p className="text-muted mb-0">Bildirim gönderme ve yönetimi</p>
      </div>

      {/* 1. BÖLÜM: Bildirim Gönderme Formu */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-4">Bildirim Gönder</h5>
          <Form onSubmit={handleSendNotification}>
            {/* Kullanıcı Seçimi */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Üye Seçin</Form.Label>
                <div className="mb-2">
                  <Button
                    variant={formData.selectedUsers.length === 0 ? "primary" : "outline-primary"}
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, selectedUsers: [] }));
                      setSelectAll(false);
                    }}
                  >
                    Herkese Gönder
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    onClick={() => setShowUserSelect(!showUserSelect)}
                  >
                    {showUserSelect ? "Listeyi Gizle" : "Üye Seç"}
                  </Button>
                </div>

                {showUserSelect && (
                  <Card className="mt-2" style={{ maxHeight: "300px", overflow: "auto" }}>
                    <Card.Body>
                      <InputGroup className="mb-3">
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faSearch} />
                        </InputGroup.Text>
                        <FormControl
                          placeholder="Üye ara..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>

                      <div className="mb-2">
                        <FormCheck
                          type="checkbox"
                          label="Tümünü Seç"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </div>

                      <div style={{ maxHeight: "200px", overflow: "auto" }}>
                        {filteredUsers.map(user => (
                          <div key={user.id} className="mb-2">
                            <FormCheck
                              type="checkbox"
                              label={`${user.name} ${user.email ? `(${user.email})` : ''}`}
                              checked={formData.selectedUsers.includes(user.id)}
                              onChange={() => handleUserSelect(user.id)}
                            />
                          </div>
                        ))}
                      </div>

                      {formData.selectedUsers.length > 0 && (
                        <div className="mt-2">
                          <Badge bg="primary">
                            {formData.selectedUsers.length} üye seçildi
                          </Badge>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>

            {/* Başlık */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Başlık <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Bildirim başlığı"
                    value={formData?.title || ""}
                    onChange={(e) => {
                      const value = e?.target?.value || "";
                      setFormData(prev => ({
                        ...prev,
                        title: value
                      }));
                    }}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Mesaj */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Mesaj <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Bildirim mesajı"
                    value={formData?.message || ""}
                    onChange={(e) => {
                      const value = e?.target?.value || "";
                      setFormData(prev => ({
                        ...prev,
                        message: value
                      }));
                    }}
                    required
                  />
                  <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          message: (prev.message || "") + "{{first_uye_name}} {{uye_sehir_da}}"
                        }));
                      }}
                      title="Kullanıcının adı ve şehrine göre akıllı etiket ekler"
                    >
                      🏷️ Akıllı Mesaj (Ad + Şehir) Ekle
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          message: (prev.message || "") + "{{first_uye_name}}"
                        }));
                      }}
                    >
                      👤 İsim Ekle
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          message: (prev.message || "") + "{{uye_sehir_da}}"
                        }));
                      }}
                    >
                      📍 Şehir Ekle
                    </Button>
                    <small className="text-muted">
                      Örn: "Merhaba {'{{first_uye_name}}'}, {'{{uye_sehir_da}}'} yeni eşleşmeler seni bekliyor!"
                    </small>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Zamanlama */}
            <Row className="mb-3">
              <Col md={12}>
                <FormCheck
                  type="checkbox"
                  label="Bildirimi Zamanla"
                  checked={formData.scheduleTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduleTime: e.target.checked }))}
                />
              </Col>
            </Row>

            {formData.scheduleTime && (
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Saat (Türkiye Saati)</Form.Label>
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Control
                        type="number"
                        min="0"
                        max="23"
                        value={formData.scheduledHour}
                        onChange={(e) => setFormData(prev => ({ ...prev, scheduledHour: e.target.value }))}
                        style={{ width: "80px" }}
                      />
                      <span>:</span>
                      <Form.Control
                        type="number"
                        min="0"
                        max="59"
                        value={formData.scheduledMinute}
                        onChange={(e) => setFormData(prev => ({ ...prev, scheduledMinute: e.target.value }))}
                        style={{ width: "80px" }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <FormCheck
                      type="checkbox"
                      label="Günlük Gönder"
                      checked={formData.isDaily}
                      onChange={(e) => setFormData(prev => ({ ...prev, isDaily: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <div className="d-flex justify-content-end">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? "Gönderiliyor..." : "Gönder"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* 2. BÖLÜM: Planlanmış Bildirimler */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Planlanmış Bildirimler</h5>
            <div className="d-flex gap-2 align-items-center">
              <Form.Select
                size="sm"
                style={{ width: "100px" }}
                value={scheduledPageSize}
                onChange={(e) => {
                  setScheduledPageSize(parseInt(e.target.value));
                  setScheduledPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
              </Form.Select>
            </div>
          </div>

          {getScheduledNotificationsPage().length === 0 ? (
            <p className="text-muted">Planlanmış bildirim bulunmuyor.</p>
          ) : (
            <>
              <Table className="table-hover">
                <thead>
                  <tr>
                    <th>Başlık</th>
                    <th>Mesaj</th>
                    <th>Saat</th>
                    <th>Tip</th>
                    <th>Hedef</th>
                  </tr>
                </thead>
                <tbody>
                  {getScheduledNotificationsPage().map(notification => (
                    <tr key={notification.id}>
                      <td><strong>{notification.title}</strong></td>
                      <td><small className="text-muted">{notification.message}</small></td>
                      <td>{formatTime(notification.scheduledTime)}</td>
                      <td>
                        <Badge bg={notification.isDaily ? "success" : "info"}>
                          {notification.isDaily ? "Günlük" : "Tek Seferlik"}
                        </Badge>
                      </td>
                      <td>
                        {notification.userIds ? (
                          <Badge bg="secondary">{notification.userIds.length} kullanıcı</Badge>
                        ) : (
                          <Badge bg="primary">Herkes</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {scheduledTotalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.Prev
                      disabled={scheduledPage === 1}
                      onClick={() => setScheduledPage(prev => Math.max(1, prev - 1))}
                    />
                    {[...Array(scheduledTotalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={scheduledPage === i + 1}
                        onClick={() => setScheduledPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={scheduledPage === scheduledTotalPages}
                      onClick={() => setScheduledPage(prev => Math.min(scheduledTotalPages, prev + 1))}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* 3. BÖLÜM: Gönderilen Bildirimler */}
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">Gönderilen Bildirimler</h5>

          {sentNotifications.length === 0 ? (
            <p className="text-muted">Henüz gönderilmiş bildirim bulunmuyor.</p>
          ) : (
            <div>
              {sentNotifications.map(notification => (
                <div key={notification.id} className="mb-3 p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <strong>{notification.title}</strong>
                      <p className="mb-1 text-muted">{notification.message}</p>
                      <small className="text-muted">
                        {notification.sentCount || 0} kullanıcıya gönderildi
                        {notification.sentAt && ` • ${formatDate(notification.sentAt)}`}
                      </small>
                    </div>
                    <Badge bg="success">Gönderildi</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

